import { useEffect, ReactNode } from "react";
import { Box, Stack, Divider, Skeleton } from "@mui/material";
import { Trans, useTranslation } from "next-i18next";
import { useWikifolioPriceQuery } from "src/api/client/wikifolio-price.api";
import { IPriceResponse } from "src/api/client/wikifolio-price.api.types";
import { IconInfoOuterCircle } from "src/components/icons-mui/info-outer-circle";
import { Typography } from "src/components-mui/base/typography/typography";
import { PopoverButtonIconInfo } from "src/components-mui/base/wf-popover/popover-button-icon-info";
import { WfPopover } from "src/components-mui/base/wf-popover/wf-popover";
import { InfoBox } from "src/components-mui/pages/detail/certificate-box/components/info-box";
import { useCertificateBoxContext } from "src/components-mui/pages/detail/certificate-box/context";
import { getGlobals } from "src/constants/globals";
import { useDateTimeFormatter } from "src/hooks/formatters/use-date-time-formatter";
import { useNumberFormatter } from "src/hooks/formatters/use-number-formatter";
import { useDetailPageStore } from "src/stores/detail-page-store";

const CERTIFICATE_BOX_MID_PRICE_TEST_ID = "certificate-box-mid-price";
const CERTIFICATE_BOX_BID_PRICE_TEST_ID = "certificate-box-bid-price";
const CERTIFICATE_BOX_ASK_PRICE_TEST_ID = "certificate-box-ask-price";
const CERTIFICATE_BOX_PRICE_INFO_TEST_ID = "certificate-box-price-info";

interface IPriceProps {
    label: string;
    value?: number | null;
    isLoading: boolean;
    fontSize?: string;
}

const Price = ({ value, label, isLoading, fontSize, ...boxProps }: IPriceProps) => {
    const { formatNumber } = useNumberFormatter();

    let renderValue: ReactNode = <Skeleton variant="rectangular" width="80px" height="22px" sx={{ my: 0.5, display: "inline-block" }} />;
    if (!isLoading) {
        renderValue = (
            <Typography
                component="strong"
                sx={{
                    fontWeight: "bold",
                    fontSize: fontSize || "2xl",
                }}
            >
                {value ? formatNumber(value, 2) : "-"}
            </Typography>
        );
    }

    return (
        <Box
            sx={{ textAlign: "center", minWidth: "72px", width: "100%" }}
            aria-label={`${label}: ${value ? formatNumber(value, 2) : "not available"}`}
            {...boxProps}
        >
            {renderValue}
            <Typography
                sx={{
                    fontSize: "sm",
                    color: "gray.600",
                }}
            >
                {label}
            </Typography>
        </Box>
    );
};

const DotDivider = () => (
    <Box
        sx={{
            mx: "5px",
            borderRadius: "50%",
            width: "2px",
            height: "2px",
            bgcolor: "currentColor",
        }}
    />
);

// depending on how large the ask or big price is, we calculate the font size so that it fits into the UI
function getAskBidFontSize(ask: number, bid: number) {
    const maxValue = Math.max(ask, bid);

    let fontSize = "2xl";
    if (maxValue >= 1000000) {
        fontSize = "lg";
    } else if (maxValue >= 10000) {
        fontSize = "xl";
    }
    return fontSize;
}

function getFormattedPriceData(isLoading: boolean, priceData: IPriceResponse | undefined) {
    if (isLoading) {
        return {
            showMidPrice: false,
            isTicking: true,
        };
    }

    const { ask, bid, midPrice, isTicking: _isTicking, showMidPrice, calculationDate: _calcDate, isCurrencyConverted } = priceData!;

    let isTicking = true;
    if (!midPrice && !_isTicking) {
        isTicking = false;
    }

    return {
        ask,
        bid,
        midPrice,
        showMidPrice,
        calculationDate: new Date(_calcDate),
        isTicking,
        isCurrencyConverted,
    };
}

function calculateIndexLevel(bid: number, ask: number, exchangeRatioMultiplier: number): number {
    return (bid + ask) / 2.0 / exchangeRatioMultiplier;
}

export const CBPrices = () => {
    const { dict, data, selectedCertificate: certificate } = useCertificateBoxContext();
    const { setIndexLevel } = useDetailPageStore();
    const exchangeRatioMultiplier = certificate.exchangeRatioMultiplier;
    const nonPrimaryIsin = certificate.isPrimary ? "" : certificate.isin;
    const numOfRetries = 3;

    const { data: priceData, isError, isLoading } = useWikifolioPriceQuery(data.wikifolio.id, nonPrimaryIsin, numOfRetries);
    const { t } = useTranslation("wf-detail");
    const { formatDateShort, formatTimeShort } = useDateTimeFormatter();
    const { isTicking, bid, ask, calculationDate, showMidPrice, midPrice, isCurrencyConverted } = getFormattedPriceData(isLoading, priceData);
    useEffect(() => {
        if (bid && ask && exchangeRatioMultiplier) {
            setIndexLevel(calculateIndexLevel(bid, ask, exchangeRatioMultiplier));
        }
    }, [bid, ask, exchangeRatioMultiplier, setIndexLevel]);

    if ((!isLoading && !priceData) || isError) {
        return <CBPricesNotTicking />;
    }

    if (!isTicking) {
        return <CBPricesNotTicking />;
    }

    const askBidFontSize = getAskBidFontSize(ask || 0, bid || 0);

    return (
        <Stack spacing={1}>
            <Box aria-live="polite" aria-label="Certificate prices">
                {showMidPrice ? (
                    <Price label={dict.prices!.midRate} value={midPrice} isLoading={isLoading} data-test-id={CERTIFICATE_BOX_MID_PRICE_TEST_ID} />
                ) : (
                    <Stack
                        direction="row"
                        sx={{
                            alignItems: "stretch",
                        }}
                    >
                        <Price
                            label={dict.prices!.sell}
                            value={bid}
                            isLoading={isLoading}
                            fontSize={askBidFontSize}
                            data-test-id={CERTIFICATE_BOX_BID_PRICE_TEST_ID}
                        />
                        <Divider orientation="vertical" flexItem sx={{ borderColor: "gray.200" }} />
                        <Price
                            label={dict.prices!.buy}
                            value={ask}
                            isLoading={isLoading}
                            fontSize={askBidFontSize}
                            data-test-id={CERTIFICATE_BOX_ASK_PRICE_TEST_ID}
                        />
                    </Stack>
                )}
            </Box>
            <Box
                sx={{
                    display: "flex",
                    alignItems: "center",
                    color: "gray.600",
                    fontSize: "2xs",
                    lineHeight: "21px",
                    justifyContent: "center",
                }}
                data-test-id={CERTIFICATE_BOX_PRICE_INFO_TEST_ID}
            >
                <>
                    {isLoading ? (
                        <Skeleton variant="rectangular" width="120px" height="14px" />
                    ) : (
                        t("prices-per-xat-y", { 0: formatDateShort(calculationDate!), 1: formatTimeShort(calculationDate!) })
                    )}
                    <DotDivider />
                    in {getGlobals().currency}
                </>

                {!isLoading && isCurrencyConverted && (
                    <WfPopover
                        slotProps={{ popper: { zIndex: 1 } }}
                        content={data.tooltips.currencyConverted!.content}
                        link={data.tooltips.currencyConverted!.link}
                        PopoverTrigger={<PopoverButtonIconInfo ariaLabel={t("currency-converted")} sx={{ ml: 0.5 }} />}
                    />
                )}
            </Box>
        </Stack>
    );
};

export const CBPricesNotTicking = () => (
    <Box
        sx={{
            pb: 1,
        }}
    >
        <InfoBox icon={<IconInfoOuterCircle sx={{ mt: 0.2, color: "gray.800", width: "18px", height: "18px" }} />}>
            <Typography
                sx={{
                    fontSize: "sm",
                }}
            >
                <Trans i18nKey="certificate.currently-no-price-available" ns="wf-detail" components={{ strong: <strong /> }} />
            </Typography>
        </InfoBox>
    </Box>
);
